import { CurrencyDropdown } from "components";
import { BookkeepingLogoFileUpload } from "components/FileUpload/FileUploadWithValue";
import { LinkedUsersToBookkeeping } from "components/linkedUsers/LinkedUsersToBookkeeping";
import { LinkedWordTemplatesToBookkeeping } from "components/linkedWordTemplates/LinkedWordTemplatesToBookkeeping";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { RenderEditOptions } from "components/MainSectionTemplate/types";
import CompanyModalPicker from "components/Picker/CompanyPicker/CompanyModalPicker";
import { CompanySmallDetails } from "components/SmallDetails";
import { BookkeepingCodeInput } from "components/ValidatedFields/BookkeepingCodeInput";
import OptionalTextAreaInput, { OptionalTextAreaDetails } from "components/ValidatedFields/OptionalTextAreaInput";
import RequiredNumberInput from "components/ValidatedFields/RequiredNumberInput";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import moment from "moment";
import React from "react";
import { EInvoiceSenderComplianceMessage } from "./detail/EInvoiceSenderComplianceMessage";
import { RadioGroup } from "components/Atoms/inputs/RadioGroup";
import { BookkeepingEmailsPanel } from "bookkeeping/emails/BookkeepingEmailsPanel";
import { findCountryById } from "shared/utils/countrystateutils";
import { isNullOrEmptyString } from "shared/utils";

interface Props {
  bookkeeping: server.dto.Bookkeeping
  emails: server.dto.BookkeepingEmail[]
  onChange: React.Dispatch<React.SetStateAction<server.dto.Bookkeeping>>
  isLastVisible: boolean
  isUsedInDocuments: boolean
  einvoiceCompliance: server.dto.EInvoiceSenderCompliance;
  refetchBookkeeping: () => Promise<void>;
}

export const BookkeepingDetailSection = (props: Props) => {
  const { bookkeeping, onChange } = props;

  const canEditVisible = !props.isLastVisible || !bookkeeping.visible;
  const hideMonthOffsetRow = props.bookkeeping.monthOffset === 0;
  const hideOffset = bookkeeping.resetOnNewYear;

  const canEditCode = !props.isUsedInDocuments;
  const canEditResetOnNewYear = !props.isUsedInDocuments;
  const canEditOffset = !props.isUsedInDocuments;
  const canEditCompany = !props.isUsedInDocuments;
  const canEditBaseCurrency = !props.isUsedInDocuments;

  const displayCurrency = serverReference.currencies.find((c) => c.value === bookkeeping.baseCurrency);

  const onValueChanged = (key: string, value: any) => {
    if (key === "visible") {
      onChange(prev => ({ ...prev, visible: value }));
    } else if (key === "title") {
      onChange(prev => ({ ...prev, name: value }));
    } else if (key === "code") {
      onChange(prev => ({ ...prev, code: value }));
    } else if (key === "resetsOnNewYear") {
      if (value === true) {
        onChange(prev => ({ ...prev, resetOnNewYear: true, offset: 0 }));
      } else {
        onChange(prev => ({ ...prev, resetOnNewYear: false }));
      }
    } else if (key === "offset") {
      onChange(prev => ({ ...prev, offset: value - 1 }));
    } else if (key === "company") {
      onChange(prev => ({ ...prev, company: value }));
    } else if (key === "currency") {
      onChange(prev => ({ ...prev, baseCurrency: value }));
    } else if (key === "contactDetails") {
      onChange(prev => ({ ...prev, textForTemplate2: value }));
    } else if (key === "processedBy") {
      onChange(prev => ({ ...prev, displayOwnerOnInvoice: value }));
    } else if (key === "displayCustomerNumber") {
      onChange(prev => ({ ...prev, displayCustomerNumberOnInvoice: value }));
    } else {
      console.warn("Unknown bookkeeping key changed: " + key);
    }
  }

  const onSuccessfulUpdate = async (key: string) => {
    if (key === "company") {
      await props.refetchBookkeeping();
    }
  }

  const getUpdateBookkeepingInstruction = (bookkeeping: server.dto.Bookkeeping): server.dto.UpdateBookkeepingDetailsInstruction => ({
    baseCurrency: bookkeeping.baseCurrency,
    bookkeepingId: bookkeeping.bookkeepingId,
    code: bookkeeping.code,
    companyId: bookkeeping.company.companyId,
    offset: bookkeeping.offset,
    resetOnNewYear: bookkeeping.resetOnNewYear,
    textForTemplate2: bookkeeping.textForTemplate2,
    title: bookkeeping.name,
    visible: bookkeeping.visible,
    displayCustomerNumberOnInvoice: bookkeeping.displayCustomerNumberOnInvoice,
    displayOwnerOnInvoice: bookkeeping.displayOwnerOnInvoice,
  });

  const hideEinvoicingSection = props.einvoiceCompliance == null || props.einvoiceCompliance.value === "countryNotSupported";

  return <DetailSectionTemplate
    isInPageEditable
    onValueChanged={onValueChanged}
    onSuccessfulUpdate={onSuccessfulUpdate}
    sections={[{
      key: "bookkeeping",
      type: "regular",
      title: RESX.Bookkeeping.resxBookkeeping,
      fields: [{
        key: "visible",
        label: RESX.GeneralLabels.Visible,
        editDisabled: !canEditVisible,
        render: () => <YesNoText value={bookkeeping.visible} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: bookkeeping.visible,
          submitChange: (visible: boolean) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            visible,
          }),
        }
      }, {
        key: "title",
        label: RESX.GeneralLabels.Title,
        render: () => <div>{bookkeeping.name}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInput
            validatorKey="title"
            focusOnMount
            maxLength={serverReference.constants.bookkeeping.maxTitleLength}
            value={value}
            onChange={(title) => { onChange(title); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bookkeeping.name,
          submitChange: (title: string) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            title,
          }),
        },
      }, {
        key: "code",
        label: RESX.Bookkeeping.Code,
        supportArticleId: serverReference.supportArticleIds.bookkeepingCode,
        editDisabled: !canEditCode,
        render: () => <div>{bookkeeping.code}</div>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <BookkeepingCodeInput
            validatorKey="code"
            focusOnMount
            value={value}
            onChange={(title) => { onChange(title); }}
            onEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bookkeeping.code,
          submitChange: (code: string) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            code,
          }),
        },
      }, {
        key: "startsOnMonth",
        hidden: hideMonthOffsetRow,
        label: RESX.Bookkeeping.StartsOnMonth,
        render: () => <div>{moment.utc().month(props.bookkeeping.monthOffset).format("MMMM")}</div>,
      }, {
        key: "resetsOnNewYear",
        label: RESX.Bookkeeping.ResetOnNewYear,
        supportArticleId: serverReference.supportArticleIds.bookkeepingResetPerYear,
        editDisabled: !canEditResetOnNewYear,
        render: () => <YesNoText value={bookkeeping.resetOnNewYear} />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: bookkeeping.resetOnNewYear,
          submitChange: (resetOnNewYear: boolean) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            resetOnNewYear,
            offset: 0,
          }),
        },
      }, {
        key: "offset",
        label: RESX.Bookkeeping.StartingNumber,
        hidden: hideOffset,
        editDisabled: !canEditOffset,
        render: () => <div>{bookkeeping.offset + 1}</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<number>) => <RequiredNumberInput
            autoFocus
            width={90}
            validatorKey="amount"
            value={value}
            onChange={(v: number) => onChange(v)}
            onEnterPressed={(v: number) => onChange(v, true)}
            onEscPressed={() => cancel()}
            decimalPoints={0}
            minValue={1}
            maxValue={serverReference.constants.bookkeeping.maxOffset}
          />,
          type: "confirmOrCancelButton",
          initialValue: bookkeeping.offset + 1,
          submitChange: (offset: number) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            offset: offset - 1,
          }),
        },
      }, {
        key: "currency",
        label: RESX.Financial.Currency,
        supportArticleId: serverReference.supportArticleIds.bookkeepingCurrency,
        editDisabled: !canEditBaseCurrency,
        render: () => <div>{displayCurrency.text} ({displayCurrency.name})</div>,
        inPageEdit: {
          render: ({ value, onChange, cancel }: RenderEditOptions<server.dto.Currencies>) => <CurrencyDropdown
            value={value}
            onChange={(currency) => {
              onChange(currency, true);
            }}
            onEsc={() => cancel()}
            config={{
              focusOnMount: true,
              openMenuOnFocus: true,
            }}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: bookkeeping.baseCurrency,
          submitChange: (baseCurrency: server.dto.Currencies) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            baseCurrency,
          }),
        },
      }]
    }, {
      key: "invoicing",
      type: "regular",
      title: RESX.Invoice.Invoicing,
      fields: [{
        key: "logo",
        label: RESX.GeneralLabels.Logo,
        render: () => <BookkeepingLogoFileUpload
          bookkeepingId={props.bookkeeping.bookkeepingId}
          logo={bookkeeping.logo}
          onUploadComplete={(fileId: string, downloadUrl: string) => {
            onChange(prev => ({ ...prev, logo: { fileId, downloadUrl } }));
          }}
        />,
      }, {
        key: "company",
        label: RESX.Company.resxCompany,
        editDisabled: !canEditCompany,
        render: () => <CompanySmallDetails company={bookkeeping.company} onChange={props.refetchBookkeeping} layout="multiLine" />,
        inPageEdit: {
          hoverEdit: true,
          render: ({ onChange, cancel, value }) => <div className="main-section-template-fields-padding">
            <CompanyModalPicker
              inPageEditing={{ onChange, cancel, hasValue: Boolean(value) }}
              modalTitle={RESX.Company.resxCompany}
              addressType="administrative"
              canCreateNew={false}
            />
          </div>,
          fullWidth: false,
          type: "custom",
          initialValue: bookkeeping.company,
          submitChange: async (c: server.dto.CompanySmall) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            companyId: c.companyId,
          }),
        },
      }, {
        key: "contactDetails",
        label: RESX.GeneralLabels.ContactDetails,
        isEmpty: !bookkeeping.textForTemplate2,
        render: () => <OptionalTextAreaDetails>{bookkeeping.textForTemplate2}</OptionalTextAreaDetails>,
        inPageEdit: {
          render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <OptionalTextAreaInput
            focusOnMount
            placeholder={contactDetailsPlaceholder}
            maxLength={serverReference.constants.bookkeeping.maxTextForTemplateLength}
            value={value ?? ""}
            onChange={(text) => { onChange(text); }}
            onCtrlEnter={() => submit()}
            onEsc={() => cancel()}
          />,
          fullWidth: true,
          type: "confirmOrCancelButton",
          initialValue: bookkeeping.textForTemplate2,
          submitChange: (text: string) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            textForTemplate2: text,
          }),
        },
      }, {
        key: "companyVatNumber",
        label: RESX.RegistrationNumber.VatNumber,
        hidden: !findCountryById(bookkeeping.companyAdmCountry, serverReference).isEuMember,
        isEmpty: isNullOrEmptyString(bookkeeping.companyEuVatNumber),
        render: () => <div>{bookkeeping.companyEuVatNumber}</div>
      }, {
        key: "displayConfigurationHeader",
        hideLabel: true,
        label: "",
        hidden: !site.currentEnvironment.invoice.hasV2,
        render: () => <h3>{RESX.GeneralLabels.Configuration}</h3>
      }, {
        key: "processedBy",
        label: RESX.InvoiceDocument.ProcessedBy,
        hidden: !site.currentEnvironment.invoice.hasV2,
        render: () => <YesNoText
          yesLabel={RESX.InvoiceDocument.YesDisplayInvoiceOwner}
          value={bookkeeping.displayOwnerOnInvoice}
        />,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
            value={value}
            onChange={(v) => onChange(v, true)}
          />,
          fullWidth: true,
          type: "custom",
          initialValue: bookkeeping.displayOwnerOnInvoice,
          submitChange: (displayOwnerOnInvoice: boolean) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            displayOwnerOnInvoice,
          })
        }
      }, {
        key: "displayCustomerNumber",
        label: RESX.Company.CustomerNumber,
        render: () => {
          switch (bookkeeping.displayCustomerNumberOnInvoice) {
            case "no":
              return <div>{RESX.GeneralLabels.lblNo}</div>;
            case "displayCustomerNumber":
              return <div>{RESX.Bookkeeping.DisplayCustomCustomerNumber}</div>;
            case "displaySystemOneCompanyId":
              return <div>{RESX.Bookkeeping.DisplaySystemOneCompanyId}</div>;
          }
        },
        hidden: !site.currentEnvironment.invoice.hasV2,
        inPageEdit: {
          render: ({ value, onChange }: RenderEditOptions<server.dto.DisplayCustomerNumberOnInvoices>) => <div className="main-section-template-fields-padding">
            <RadioGroup<server.dto.DisplayCustomerNumberOnInvoices>
              orientation="horizontal"
              options={[
                { label: RESX.GeneralLabels.lblNo, value: "no" },
                { label: RESX.Bookkeeping.DisplayCustomCustomerNumber, value: "displayCustomerNumber" },
                { label: RESX.Bookkeeping.DisplaySystemOneCompanyId, value: "displaySystemOneCompanyId" }
              ]}
              value={value}
              onChange={(v) => onChange(v, true)}
            />
          </div>,
          fullWidth: true,
          type: "custom",
          initialValue: bookkeeping.displayCustomerNumberOnInvoice,
          submitChange: (displayCustomerNumberOnInvoice: server.dto.DisplayCustomerNumberOnInvoices) => window.CoreApi.Bookkeeping.UpdateDetails({
            ...getUpdateBookkeepingInstruction(bookkeeping),
            displayCustomerNumberOnInvoice,
          })
        }
      }, {
        key: "eInvoicingHeader",
        hideLabel: true,
        label: "",
        hidden: hideEinvoicingSection,
        render: () => <h3>{"e-Invoicing"}</h3>
      }, {
        key: "eInvoicingCompliance",
        label: "Peppol",
        hidden: hideEinvoicingSection,
        render: () => <EInvoiceSenderComplianceMessage compliance={props.einvoiceCompliance} />
      }]
    }, {
      key: "accountingSoftwareEmails",
      type: "custom",
      hidden: !site.currentEnvironment.invoice.hasV2,
      render: () => <BookkeepingEmailsPanel
        bookkeepingId={props.bookkeeping.bookkeepingId}
        emails={props.emails}
        onChanged={props.refetchBookkeeping}
      />
    }, {
      key: "users",
      type: "custom",
      hidden: !bookkeeping.visible,
      render: () => <LinkedUsersToBookkeeping
        key="users"
        bookkeepingId={props.bookkeeping.bookkeepingId}
      />
    }, {
      key: "wordTemplates",
      type: "custom",
      hidden: !bookkeeping.visible,
      render: () => <LinkedWordTemplatesToBookkeeping
        key="wordTemplates"
        bookkeepingId={props.bookkeeping.bookkeepingId}
      />
    }]}
  />
}

const contactDetailsPlaceholder = `${RESX.GeneralLabels.ForInstance}: ${RESX.GeneralLabels.Email} | ${RESX.ContactDetails.PhoneNumber}`;